import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import OpportunityDetailDrawer from './OpportunityDetailDrawer';
import { BACKEND_URL } from '../config';

import {
  ArrowRight,
  Clock,
  CurrencyDollar,
  Eye,
  Sparkle,
  TrendUp,
  UsersThree
} from '@phosphor-icons/react';
/**
 * Generate a meaningful role title using intelligent fallbacks
 * Rule: Never show "Untitled Role" in user-facing UI
 */
const generateRoleTitle = (role, opportunity) => {
  // Priority 1: Explicit role title (must be a string)
  if (role?.title && typeof role.title === 'string' && role.title !== 'Untitled Role') {
    return role.title;
  }
  
  // Priority 2: From opportunity's recommended agent config
  const configTitle = opportunity?.recommended_agent_config?.role_title;
  if (configTitle && typeof configTitle === 'string') {
    return configTitle;
  }
  
  // Priority 3: From opportunity title (often contains role info)
  const oppTitle = opportunity?.title;
  if (oppTitle && typeof oppTitle === 'string' && !oppTitle.toLowerCase().includes('untitled')) {
    return oppTitle;
  }
  
  // Priority 4: Generate from department/category
  const dept = role?.department || opportunity?.department || opportunity?.category;
  if (dept && typeof dept === 'string') {
    // Clean up department name and create role title
    const cleanDept = dept.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return `${cleanDept} Role`;
  }
  
  // Priority 5: From role name (person's name might indicate department)
  if (role?.name && typeof role.name === 'string') {
    return `${role.name}'s Role`;
  }
  
  // Final fallback - never "Untitled Role"
  return 'Unassigned Role';
};

/**
 * Determine organizational hierarchy level for sorting
 * Order: CEO/Executives → Department Heads → Managers → Individual Contributors
 */
const getHierarchyLevel = (role, opportunity) => {
  // Safely extract and convert title/dept to strings
  const rawTitle = role?.title || opportunity?.title;
  const rawDept = role?.department || opportunity?.department;
  
  // Ensure we only work with string values
  const title = (typeof rawTitle === 'string' ? rawTitle : '').toLowerCase();
  const dept = (typeof rawDept === 'string' ? rawDept : '').toLowerCase();
  
  // Level 1: CEO / C-Suite / Executives
  if (
    title.includes('ceo') || 
    title.includes('chief') || 
    title.includes('president') ||
    title.includes('founder') ||
    title.includes('executive') ||
    title.includes('c-level') ||
    dept === 'executive' ||
    dept === 'leadership'
  ) {
    return 1;
  }
  
  // Level 2: Department Heads / VPs / Directors
  if (
    title.includes('vp') ||
    title.includes('vice president') ||
    title.includes('director') ||
    title.includes('head of') ||
    title.includes('department head') ||
    title.includes('general manager')
  ) {
    return 2;
  }
  
  // Level 3: Managers / Team Leads
  if (
    title.includes('manager') ||
    title.includes('lead') ||
    title.includes('supervisor') ||
    title.includes('coordinator')
  ) {
    return 3;
  }
  
  // Level 4: Individual Contributors (default)
  return 4;
};

const AgentSuggestionsView = ({ 
  parsedOrgCharts, 
  orgChart,
  selectedUploadScope,
  filteredOpportunities, // Pre-filtered by parent based on Active View
  onCreateAgent,
  onViewOnChart,
  activeOrganization
}) => {
  const navigate = useNavigate();
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showOpportunityDrawer, setShowOpportunityDrawer] = useState(false);
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all opportunities from unified API
  useEffect(() => {
    fetchOpportunities();
  }, [activeOrganization]);

  const fetchOpportunities = async () => {
    if (!activeOrganization) return;
    
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/opportunities?organization_id=${activeOrganization.organization_id}&category=role`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllOpportunities(data.opportunities || []);
      }
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Use filtered opportunities if provided, otherwise use all
  // This ensures Active View scoping works correctly
  const opportunities = filteredOpportunities || allOpportunities;

  // Helper to find role by ID from org chart
  function findRoleById(roleId) {
    if (!orgChart || !orgChart.root) return null;
    
    const search = (node) => {
      if (node.id === roleId) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = search(child);
          if (found) return found;
        }
      }
      return null;
    };
    
    return search(orgChart.root);
  }

  /**
   * Sort and group opportunities by organizational hierarchy
   * Order: Executives → Department Heads → Managers → Individual Contributors
   * Within each level: Sort by priority score (descending)
   */
  const sortedOpportunitiesByHierarchy = useMemo(() => {
    const enrichedOpps = opportunities.map(opp => {
      // Support both old (linked_role_id) and new (role_id, primary_owner_role_id) formats
      const roleId = opp.linked_role_id || opp.role_id || opp.primary_owner_role_id;
      const linkedRole = findRoleById(roleId);
      return {
        ...opp,
        role: linkedRole,
        hierarchyLevel: getHierarchyLevel(linkedRole, opp),
        displayTitle: generateRoleTitle(linkedRole, opp)
      };
    });

    // Sort by hierarchy level first, then by priority score within each level
    return enrichedOpps.sort((a, b) => {
      // First: Sort by hierarchy level (ascending - executives first)
      if (a.hierarchyLevel !== b.hierarchyLevel) {
        return a.hierarchyLevel - b.hierarchyLevel;
      }
      // Second: Sort by priority score (descending - higher priority first)
      return (b.priority_score || 0) - (a.priority_score || 0);
    });
  }, [opportunities, orgChart]);

  // Group sorted opportunities by hierarchy level for display
  const groupedByHierarchy = useMemo(() => {
    const groups = {
      'Executive Leadership': [],
      'Department Heads': [],
      'Managers & Team Leads': [],
      'Individual Contributors': []
    };

    sortedOpportunitiesByHierarchy.forEach(opp => {
      switch (opp.hierarchyLevel) {
        case 1:
          groups['Executive Leadership'].push(opp);
          break;
        case 2:
          groups['Department Heads'].push(opp);
          break;
        case 3:
          groups['Managers & Team Leads'].push(opp);
          break;
        default:
          groups['Individual Contributors'].push(opp);
      }
    });

    // Filter out empty groups
    return Object.entries(groups).filter(([_, opps]) => opps.length > 0);
  }, [sortedOpportunitiesByHierarchy]);

  // Handle opportunity card click - open detailed drawer
  const handleOpportunityClick = (opportunity) => {
    // Prepare opportunity data with role context for the drawer
    const enrichedOpportunity = {
      ...opportunity,
      // Ensure role info is included for the drawer
      role_id: opportunity.role?.id || opportunity.linked_role_id,
      role_title: opportunity.displayTitle,
      role_department: opportunity.role?.department || opportunity.department,
      // Include all fields the drawer needs
      title: opportunity.title || opportunity.displayTitle,
      description: opportunity.description || opportunity.recommended_agent_config?.opportunity_summary,
    };
    
    setSelectedOpportunity(enrichedOpportunity);
    setShowOpportunityDrawer(true);
  };

  // Handle "View on Chart" click - switch to visual tab and highlight role
  const handleViewOnChartClick = (e, opp) => {
    e.stopPropagation();
    const roleId = opp.role?.id || opp.linked_role_id;
    if (roleId && onViewOnChart) {
      onViewOnChart(roleId);
    }
  };

  if (loading) {
    return (
      <div className="glass rounded-3xl p-12 text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#6366F1] mx-auto mb-4"></div>
        <p className="text-[#A0AEC0]">Loading AI opportunities...</p>
      </div>
    );
  }

  if (opportunities.length === 0) {
    return (
      <div className="glass rounded-3xl p-12 text-center">
        <Sparkle size={64} className="mx-auto mb-6 text-[#6366F1]" />
        <h2 className="text-3xl font-bold text-white mb-4">
          No AI Opportunities Yet
        </h2>
        <p className="text-xl text-[#A0AEC0] mb-8">
          {selectedUploadScope === 'all' 
            ? 'Upload and analyze your org chart to discover AI-powered automation opportunities.'
            : 'No opportunities found for this org chart. Try selecting "Combined View" or analyze this org chart.'}
        </p>
        <button
          onClick={() => navigate('/my-assessments')}
          className="relative overflow-hidden rounded-full group inline-flex"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
          <span className="relative z-10 flex items-center gap-2 px-8 py-4 text-white font-bold">
            Go to Assessments
            <ArrowRight size={20} />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Opportunities displayed by organizational hierarchy */}
      {groupedByHierarchy.map(([hierarchyName, opps]) => (
        <div key={hierarchyName} className="space-y-4">
          {/* Hierarchy Level Header */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#6366F1] to-transparent"></div>
            <h2 className="text-2xl font-bold text-white">{hierarchyName}</h2>
            <span className="text-sm text-[#A0AEC0]">({opps.length} opportunities)</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#6366F1] to-transparent"></div>
          </div>
          
          {/* Opportunities Grid */}
          <div className="grid md:grid-cols-3 gap-4">
            {opps.map((opp) => {
              const highPriority = (opp.priority_score || 0) > 60;
              const roleId = opp.role?.id || opp.linked_role_id;
              const affectsMultipleRoles = opp.applies_to_roles_count && opp.applies_to_roles_count > 1;
              
              return (
                <div 
                  key={opp.id} 
                  onClick={() => handleOpportunityClick(opp)}
                  className="glass rounded-xl p-4 hover:bg-[rgba(255,255,255,0.05)] transition-all cursor-pointer hover:border-[#6366F1] border border-transparent group"
                >
                  {/* Role Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1">
                        {opp.displayTitle}
                      </h3>
                      {opp.role?.name && opp.role.name !== opp.displayTitle && (
                        <p className="text-sm text-[#E2E8F0]">{opp.role.name}</p>
                      )}
                      {opp.role?.department && (
                        <p className="text-xs text-[#A0AEC0]">{opp.role.department}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Multi-role badge */}
                      {affectsMultipleRoles && (
                        <div className="px-2 py-1 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6] text-xs font-semibold flex items-center gap-1">
                          <UsersThree size={12} />
                          {opp.applies_to_roles_count} roles
                        </div>
                      )}
                      {highPriority && (
                        <div className="w-8 h-8 rounded-full bg-[#F59E0B] flex items-center justify-center">
                          <Sparkle size={16} className="text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* AI Opportunity Summary */}
                  <div className="mb-3">
                    <div className="flex items-center gap-1 mb-2">
                      <Sparkle size={14} className="text-[#F59E0B]" />
                      <p className="text-xs text-[#F59E0B] font-semibold">AI Opportunity</p>
                      <span className="ml-auto px-2 py-0.5 bg-[rgba(245,158,11,0.2)] text-[#F59E0B] rounded-full text-xs font-semibold">
                        Priority: {opp.priority_score || 'N/A'}
                      </span>
                    </div>
                    <p className="text-sm text-[#E2E8F0] line-clamp-3">
                      {opp.description || opp.recommended_agent_config?.opportunity_summary || 'AI automation opportunity identified for this role.'}
                    </p>
                  </div>
                  
                  {/* Quick Metrics - NEVER show blank values */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center">
                      <TrendUp size={14} className="mx-auto text-green-400 mb-1" />
                      <p className="text-xs text-[#A0AEC0]">Impact</p>
                      <p className="text-sm font-semibold text-white">
                        {opp.impact != null ? opp.impact : '—'}
                      </p>
                    </div>
                    <div className="text-center">
                      <Clock size={14} className="mx-auto text-amber-400 mb-1" />
                      <p className="text-xs text-[#A0AEC0]">Effort</p>
                      <p className="text-sm font-semibold text-white">
                        {opp.effort != null ? opp.effort : 'Medium'}
                      </p>
                    </div>
                    <div className="text-center">
                      <CurrencyDollar size={14} className="mx-auto text-emerald-400 mb-1" />
                      <p className="text-xs text-[#A0AEC0]">ROI</p>
                      <p className="text-sm font-semibold text-white">
                        {opp.roi?.roi_percentage ? `${Math.round(opp.roi.roi_percentage)}%` : '—'}
                      </p>
                    </div>
                  </div>
                  
                  {/* ROI Preview */}
                  {opp.roi && opp.roi.annual_savings > 0 && (
                    <div className="mt-2 text-xs text-[#10B981] bg-[rgba(16,185,129,0.1)] px-2 py-1 rounded">
                      Est. Annual Savings: ${opp.roi.annual_savings?.toLocaleString()}
                    </div>
                  )}
                  
                  {/* Click hint */}
                  <p className="text-xs text-[#6366F1] mt-3 group-hover:text-[#818CF8] transition-colors">
                    Click to view details and create agent →
                  </p>
                  
                  {/* Quick Actions */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-[rgba(255,255,255,0.1)]">
                    <button
                      onClick={(e) => handleViewOnChartClick(e, opp)}
                      disabled={!roleId}
                      className={`px-3 py-2 text-xs glass rounded-lg transition-all font-semibold flex items-center gap-1 ${
                        roleId 
                          ? 'hover:bg-[rgba(255,255,255,0.1)] text-[#A0AEC0] hover:text-white cursor-pointer' 
                          : 'text-[#5C6778] cursor-not-allowed'
                      }`}
                      title={roleId ? "View on org chart" : "Role not linked to chart"}
                    >
                      <Eye size={12} />
                      View on chart
                      <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Opportunity Detail Drawer - Full featured, same as used elsewhere */}
      <OpportunityDetailDrawer
        opportunity={selectedOpportunity}
        isOpen={showOpportunityDrawer}
        onClose={() => {
          setShowOpportunityDrawer(false);
          setSelectedOpportunity(null);
        }}
        onProceedToAgent={(opp) => {
          // Drawer handles navigation to /partner-pro/ai-workforce/agents/create
          // Just close the drawer here
          setShowOpportunityDrawer(false);
          setSelectedOpportunity(null);
        }}
      />
    </div>
  );
};

export default AgentSuggestionsView;
